import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
    BookOpen, Calendar, Eye, ChevronLeft, Tag, Share2,
    Loader2, User, Clock
} from 'lucide-react';
import { apiRequest } from '../lib/utils';
import SEOHead from '../components/SEOHead';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

export default function ArticlePage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedArticles, setRelatedArticles] = useState([]);

    useEffect(() => {
        loadArticle();
    }, [slug]);

    const loadArticle = async () => {
        setLoading(true);
        try {
            const data = await apiRequest(`/blog/articles/${slug}`);
            setArticle(data);
            
            // Load related articles from same category
            if (data.category) {
                const related = await apiRequest(`/blog/articles?category=${data.category}&limit=3`);
                setRelatedArticles(related.filter(a => a.slug !== slug).slice(0, 2));
            }
        } catch (error) {
            console.error('Failed to load article:', error);
            navigate('/blog');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const handleShare = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: article.title,
                    text: article.excerpt,
                    url: url
                });
            } catch (err) {
                // User cancelled or error
            }
        } else {
            navigator.clipboard.writeText(url);
            toast.success('Lien copié !');
        }
    };

    const estimateReadTime = (content) => {
        const wordsPerMinute = 200;
        const words = content.split(/\s+/).length;
        const minutes = Math.ceil(words / wordsPerMinute);
        return minutes;
    };

    if (loading) {
        return (
            <div className="min-h-screen pt-20 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!article) {
        return null;
    }

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="article-page">
            {/* SEO */}
            <SEOHead 
                title={article.meta_title || `${article.title} | O.C.A Piscine`}
                description={article.meta_description || article.excerpt}
                keywords={article.tags?.join(', ')}
                canonicalUrl={`https://www.ocapiscine.fr/blog/${article.slug}`}
                ogImage={article.image_url}
                ogType="article"
            />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Link */}
                <Link 
                    to="/blog" 
                    className="inline-flex items-center text-muted-foreground hover:text-primary mb-6 transition-colors"
                >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Retour aux articles
                </Link>

                {/* Article Header */}
                <article>
                    {/* Category & Tags */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <Badge variant="default" className="rounded-full">
                            {article.category}
                        </Badge>
                        {article.tags?.slice(0, 3).map(tag => (
                            <Badge key={tag} variant="secondary" className="rounded-full">
                                <Tag className="h-3 w-3 mr-1" />
                                {tag}
                            </Badge>
                        ))}
                    </div>

                    {/* Title */}
                    <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                        {article.title}
                    </h1>

                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-8">
                        <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span>{article.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>{formatDate(article.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>{estimateReadTime(article.content)} min de lecture</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Eye className="h-4 w-4" />
                            <span>{article.views} vues</span>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={handleShare}
                            className="rounded-full"
                        >
                            <Share2 className="h-4 w-4 mr-1" />
                            Partager
                        </Button>
                    </div>

                    {/* Featured Image */}
                    {article.image_url && (
                        <div className="aspect-video rounded-2xl overflow-hidden mb-8">
                            <img 
                                src={article.image_url} 
                                alt={article.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Excerpt */}
                    <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                        {article.excerpt}
                    </p>

                    {/* Content */}
                    <Card className="glass rounded-3xl">
                        <CardContent className="p-6 sm:p-8 lg:p-12">
                            <div className="prose prose-lg dark:prose-invert max-w-none
                                prose-headings:font-heading prose-headings:font-bold
                                prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
                                prose-p:text-muted-foreground prose-p:leading-relaxed
                                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                                prose-strong:text-foreground
                                prose-ul:text-muted-foreground prose-ol:text-muted-foreground
                                prose-li:marker:text-primary
                                prose-table:border-collapse prose-th:bg-muted prose-th:p-3 prose-td:p-3 prose-td:border
                            ">
                                <ReactMarkdown>{article.content}</ReactMarkdown>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                        <div className="mt-8">
                            <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                                Tags
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {article.tags.map(tag => (
                                    <Link 
                                        key={tag} 
                                        to={`/blog?tag=${encodeURIComponent(tag)}`}
                                    >
                                        <Badge 
                                            variant="outline" 
                                            className="rounded-full hover:bg-muted transition-colors"
                                        >
                                            <Tag className="h-3 w-3 mr-1" />
                                            {tag}
                                        </Badge>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </article>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                    <div className="mt-12">
                        <h2 className="font-heading text-2xl font-bold mb-6">
                            Articles similaires
                        </h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {relatedArticles.map(related => (
                                <Link key={related.id} to={`/blog/${related.slug}`}>
                                    <Card className="glass rounded-2xl h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                        <CardContent className="p-6">
                                            <Badge variant="secondary" className="mb-3">
                                                {related.category}
                                            </Badge>
                                            <h3 className="font-heading font-bold mb-2 hover:text-primary transition-colors">
                                                {related.title}
                                            </h3>
                                            <p className="text-muted-foreground text-sm line-clamp-2">
                                                {related.excerpt}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* CTA */}
                <Card className="glass rounded-3xl mt-12 overflow-hidden bg-gradient-to-r from-primary/10 to-blue-500/10">
                    <CardContent className="p-8 text-center">
                        <BookOpen className="h-12 w-12 mx-auto mb-4 text-primary" />
                        <h2 className="font-heading text-2xl font-bold mb-4">
                            Besoin de produits pour votre piscine ?
                        </h2>
                        <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                            Découvrez notre gamme complète de produits CTX pour l'entretien de votre piscine. 
                            Livraison rapide dans tout le Var.
                        </p>
                        <Link to="/shop">
                            <Button className="rounded-full" size="lg">
                                Voir la boutique
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
